import LockIcon from '@mui/icons-material/Lock';
import SearchIcon from '@mui/icons-material/Search';
import {
  type AutocompleteInputChangeReason,
  InputAdornment,
  Autocomplete as MuiAutocomplete,
  Popper as MuiPopper,
  type PopperProps,
  type SxProps,
  TextField,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { Icon } from '@ovComponents/1-primative/icon/icon';
import { Box, CircularProgress, Tooltip, useTheme } from 'hephaestus/web';
import {
  type CSSProperties,
  type ReactNode,
  type SyntheticEvent,
  type UIEvent,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { useTranslation } from 'react-i18next';
import { FieldLabel, useFieldIds } from '../fieldLabel/fieldLabel';
import { HelpText } from '../helpText/helpText';
import { useHelpTextTokens } from '../helpText/helpText.tokens';
import { useTextFieldTokens } from '../textField/textField.tokens';
import { useAutocompleteSearch } from './useAutocompleteSearch';

const isEmptyAutocompleteValue = (value: unknown): boolean => {
  if (value == null) return true;
  if (Array.isArray(value)) return value.length === 0;
  return false;
};
export type AutocompleteProps<T = any> = {
  id?: string;
  name?: string;
  label: string;
  open?: boolean;
  helpText?: string;
  error?: boolean;
  errorText?: string;
  success?: boolean;
  successText?: string;
  fullWidth?: boolean;
  disabled?: boolean;
  /** Required for sync mode; optional when `onSearch` is provided (may inject selected option). */
  options?: T[];
  isOptionEqualToValue?: any;
  value?: any;
  /** Kept loose for backward compatibility with existing call sites. */
  onChange?: any;
  onInputChange?: any;
  onFocus?: any;
  onClick?: any;
  onBlur?: any;
  multiple?: boolean;
  freeSolo?: boolean;
  renderTags?: any;
  renderOption?: any;
  sx?: SxProps;
  labelSx?: SxProps;
  InputProps?: object;
  ChipProps?: object;
  getOptionLabel?: (option: any) => string;
  width?: string;
  componentsProps?: object;
  inputReadOnly?: boolean;
  filterOptions?: (options: any[], state: object) => any[];
  loading?: boolean;
  loadingText?: string;
  noOptionsText?: string;
  autoFocus?: boolean;
  blurOnSelect?: boolean;
  disableClearable?: boolean;
  infoTooltip?: string;
  size?: 'small' | 'medium';
  testId?: string;
  lockMessage?: string;
  locked?: boolean;
  disableCloseOnSelect?: boolean;
  showSearchIcon?: boolean;
  placeholder?: string;
  required?: boolean;
  /** When provided, enables async mode (debounce + loading + server-driven options). */
  onSearch?: (query: string) => Promise<T[]>;
  /** Next-page fetch for the current query; enables scroll-append with hasMore. */
  onSearchMore?: () => Promise<T[]>;
  /** Whether more pages exist for the current query. */
  hasMore?: boolean;
  /** Static footer under the suggestion list (e.g. result counts). */
  listFooter?: ReactNode;
  listFooterTestId?: string;
  loadingMoreText?: string;
  debounceMs?: number;
  minChars?: number;
  searchOnOpen?: boolean;
  onSearchError?: (error: unknown) => void;
};

const StyledTextField = styled(TextField)(() => {
  const theme = useTheme();
  const tokens = useTextFieldTokens(theme);

  return {
    '& .MuiOutlinedInput-root': {
      width: '100%',
      paddingLeft: '12px',
      paddingRight: '14px !important',
      '& input': {
        padding: '0px !important',
        color: tokens.textColor,
      },
      '& fieldset': {
        borderColor: tokens.borderColor,
      },
      '&.Mui-focused fieldset': {
        borderColor: tokens.focusBorderColor,
      },
      '& .MuiSelect-select': {
        paddingLeft: '0px',
        marginLeft: '12px',
        marginRight: '12px',
      },
      '& .MuiInputAdornment-positionEnd': {
        marginLeft: '0px',
      },
      '&.Mui-error': {
        '& input': {
          color: tokens.errorColor,
        },
        '& fieldset': {
          borderColor: tokens.errorColor,
        },
      },
    },
  };
});

const identityFilterOptions = <T,>(options: T[]) => options;

// Footer under the suggestion list. Reads live content from a ref (not props)
// so the Popper wrapper keeps a stable identity and never remounts the list
// (which would reset scroll) when the footer updates. Mousedown is swallowed
// so interacting with the footer neither selects nor dismisses the list.
type PopperFooterState = {
  content: ReactNode;
  loading: boolean;
  loadingText: string;
  testId?: string;
};

const PopperFooterSlot = ({
  stateRef,
}: {
  stateRef: React.MutableRefObject<PopperFooterState>;
}) => {
  const { content, loading, loadingText, testId } = stateRef.current;
  if (!content && !loading) return null;
  return (
    <Box
      role='presentation'
      data-testid={testId}
      onMouseDown={(event) => event.preventDefault()}
      sx={(theme) => ({
        position: 'relative',
        ...(content && {
          backgroundColor: theme.palette.background.paper,
          borderTop: `1px solid ${theme.palette.divider}`,
          padding: theme.spacing(1, 2),
        }),
      })}
    >
      {content}
      {loading && (
        // Overlays the bottom of the list: growing the popper while paging
        // makes the list jump under the cursor.
        <Box
          sx={(theme) => ({
            position: 'absolute',
            left: 0,
            right: 0,
            bottom: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1,
            padding: theme.spacing(1, 2),
            backgroundColor: theme.palette.background.paper,
            borderTop: `1px solid ${theme.palette.divider}`,
          })}
        >
          <CircularProgress color='inherit' size={16} />
          {loadingText}
        </Box>
      )}
    </Box>
  );
};

export const Autocomplete = <T,>({
  id,
  name,
  error = false,
  label,
  open,
  options,
  fullWidth,
  errorText,
  helpText,
  successText,
  success = false,
  isOptionEqualToValue,
  value,
  onChange,
  onFocus,
  onClick,
  multiple,
  renderTags,
  renderOption,
  onInputChange,
  disabled,
  InputProps,
  ChipProps,
  getOptionLabel,
  width,
  sx,
  onBlur,
  componentsProps,
  filterOptions,
  loading: loadingProp = false,
  loadingText,
  noOptionsText,
  autoFocus,
  inputReadOnly = false,
  freeSolo = false,
  blurOnSelect = false,
  infoTooltip,
  size,
  testId,
  lockMessage,
  locked,
  disableCloseOnSelect,
  disableClearable,
  showSearchIcon = false,
  placeholder,
  required,
  onSearch,
  onSearchMore,
  hasMore = false,
  listFooter,
  listFooterTestId,
  loadingMoreText,
  debounceMs = 300,
  minChars = 0,
  searchOnOpen = true,
  onSearchError,
}: AutocompleteProps<T>) => {
  const variantStyle: CSSProperties = {};
  const theme = useTheme();
  const tokens = useTextFieldTokens(theme);
  const helpTextTokens = useHelpTextTokens(theme);
  const { t } = useTranslation('components');
  const { inputId, labelId } = useFieldIds(id);
  const labelledBy = label ? labelId : undefined;

  const {
    isAsync,
    asyncOptions,
    internalLoading,
    loadingMore,
    belowMinChars,
    runSearch,
    loadMore,
    handleOpen,
    reset,
  } = useAutocompleteSearch<T>({
    onSearch,
    onSearchMore,
    hasMore,
    debounceMs,
    minChars,
    searchOnOpen,
    onSearchError,
  });

  const prevValueRef = useRef(value);
  useEffect(() => {
    if (!isAsync) return;
    const wasEmpty = isEmptyAutocompleteValue(prevValueRef.current);
    const isEmpty = isEmptyAutocompleteValue(value);
    prevValueRef.current = value;
    // Parent cleared selection without an input clear (e.g. Recon Flags "Clear filters")
    if (!wasEmpty && isEmpty) {
      reset();
    }
  }, [isAsync, value, reset]);

  const loading = loadingProp || internalLoading;

  const resolvedOptions = useMemo(() => {
    // Hide previous suggestions while loading so the list only shows the loading state
    if (loading) return [];
    if (!isAsync) return options ?? [];
    // Async list only — do not merge in parent/selected options (avoids prior
    // selection sticking at the top of a new search).
    return Array.isArray(asyncOptions) ? asyncOptions : [];
  }, [isAsync, loading, options, asyncOptions]);

  const resolvedFilterOptions =
    filterOptions ?? (isAsync ? identityFilterOptions : undefined);

  const resolvedNoOptionsText = (() => {
    if (noOptionsText !== undefined) return noOptionsText;
    if (belowMinChars) {
      return minChars > 1
        ? t('autocomplete.minChars', { count: minChars })
        : t('autocomplete.typeToSearch');
    }
    return t('autocomplete.noOptions');
  })();

  const resolvedLoadingText = (
    <Box
      data-testid={`${testId ?? 'auto-complete'}-loading`}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
      }}
    >
      <CircularProgress color='inherit' size='small' />
      {loadingText ?? t('autocomplete.loading')}
    </Box>
  );

  const style: CSSProperties = {
    ...variantStyle,
    height: 'fit-content',
    color: tokens.textColor,
    fontSize: '16px',
    fontFamily: theme.font.base,
    borderRadius: '4px',
    width: fullWidth ? '100%' : width || 'auto',
    // Avoid collapsing to icon-only width when not fullWidth / no explicit width
    ...(fullWidth ? {} : { minWidth: width ?? '200px' }),
  };

  const shouldDisplayInDialog = helpTextTokens.displayInDialog;
  const helpTextToShow =
    infoTooltip && !shouldDisplayInDialog ? infoTooltip : helpText;

  const helpTextFunc = (isError: boolean, isSuccess: boolean) => {
    if (isError) {
      return (
        (errorText ?? t('fieldErrors.default', { fieldName: label })) ||
        helpTextToShow
      );
    }

    if (isSuccess) {
      return successText || helpTextToShow;
    }

    return helpTextToShow;
  };

  const handleInputChange = (
    event: SyntheticEvent,
    inputValue: string,
    reason: AutocompleteInputChangeReason,
  ) => {
    if (isAsync) {
      if (reason === 'clear') {
        reset();
      } else if (reason === 'input') {
        runSearch(inputValue);
      }
    }
    onInputChange?.(event, inputValue, reason);
  };

  const handleClose = () => {
    if (isAsync) {
      // Drop last search hits so reopening doesn't show a stale suggestion list
      reset();
    }
  };

  // Scroll-append: fetch the next page when the list is scrolled near the
  // bottom. The hook guards against duplicate and stale appends.
  const handleListScroll = (event: UIEvent<HTMLElement>) => {
    if (!onSearchMore || !hasMore) return;
    const el = event.currentTarget;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 48) {
      loadMore();
    }
  };

  // Footer state flows through a single stable ref: the footer element is
  // recreated each render, but the wrapper identity below never changes, so
  // footer updates (count text, spinner) never remount the list.
  const footerStateRef = useRef<PopperFooterState>({
    content: null,
    loading: false,
    loadingText: '',
  });
  footerStateRef.current = {
    content: !internalLoading && hasMore ? (listFooter ?? null) : null,
    loading: loadingMore,
    loadingText: loadingMoreText ?? t('autocomplete.loadingMore'),
    testId: listFooterTestId,
  };
  // Presence boolean only: the closure captures just the stable ref, so even
  // an inline onSearchMore cannot remount the list on every parent render.
  const hasFooterFeature = Boolean(listFooter) || Boolean(onSearchMore);
  const popperComponent = useMemo(
    () =>
      hasFooterFeature
        ? ({ children, ...popperProps }: PopperProps) => (
            <MuiPopper {...popperProps}>
              {children as ReactNode}
              <PopperFooterSlot stateRef={footerStateRef} />
            </MuiPopper>
          )
        : undefined,
    [hasFooterFeature],
  );

  return (
    <Box sx={{ ...sx } as any}>
      {label ? (
        <FieldLabel
          htmlFor={inputId}
          id={labelledBy}
          error={error}
          required={required}
          infoTooltip={infoTooltip}
          shouldDisplayInDialog={shouldDisplayInDialog}
        >
          {label}
        </FieldLabel>
      ) : null}
      <MuiAutocomplete
        data-testid={testId ?? 'auto-complete'}
        renderInput={(params: any) => {
          const parentInputProps = (InputProps ?? {}) as {
            startAdornment?: ReactNode;
            endAdornment?: ReactNode;
          };
          const parentProvidesEndAdornment =
            InputProps != null && 'endAdornment' in InputProps;

          let endAdornment: ReactNode = params.InputProps.endAdornment;
          if (locked) {
            endAdornment = (
              <InputAdornment position='end'>
                <Tooltip title={lockMessage ?? ''}>
                  <Icon icon={LockIcon} size='small' />
                </Tooltip>
              </InputAdornment>
            );
          } else if (parentProvidesEndAdornment) {
            // Parent `endAdornment: null` hides popup/clear (e.g. address fields).
            // Parent adornment while disabled replaces MUI icons (lock only).
            // Otherwise compose parent + MUI clear/popup (e.g. securities spinner).
            if (parentInputProps.endAdornment == null || disabled) {
              endAdornment = parentInputProps.endAdornment;
            } else {
              endAdornment = (
                <>
                  {parentInputProps.endAdornment}
                  {params.InputProps.endAdornment}
                </>
              );
            }
          }

          return (
            <StyledTextField
              ref={params?.InputProps.ref}
              {...params}
              id={inputId}
              name={name}
              error={!!error}
              placeholder={placeholder}
              InputProps={{
                ...params.InputProps,
                ...InputProps,
                ...(showSearchIcon
                  ? {
                      startAdornment: (
                        <>
                          <InputAdornment position='start'>
                            <SearchIcon />
                          </InputAdornment>
                          {params.InputProps.startAdornment}
                        </>
                      ),
                    }
                  : {}),
                endAdornment,
              }}
              style={style}
              inputProps={{
                ...params.inputProps,
                id: inputId,
                name,
                'aria-labelledby': labelledBy,
                disabled: inputReadOnly || disabled || locked,
                'data-testid': `${testId ?? 'auto-complete'}-input`,
              }}
              autoFocus={autoFocus}
            />
          );
        }}
        open={open}
        sx={
          {
            '& + .MuiAutocomplete-popper .MuiAutocomplete-option': {
              color: tokens.textColor,
            },
          } as any
        }
        freeSolo={freeSolo}
        options={resolvedOptions}
        isOptionEqualToValue={isOptionEqualToValue}
        value={value}
        onChange={onChange}
        onInputChange={handleInputChange}
        onOpen={isAsync ? handleOpen : undefined}
        onClose={isAsync ? handleClose : undefined}
        ListboxProps={onSearchMore ? { onScroll: handleListScroll } : undefined}
        PopperComponent={popperComponent}
        onFocus={onFocus}
        onClick={onClick}
        onBlur={onBlur}
        fullWidth={fullWidth}
        style={style}
        multiple={multiple}
        renderTags={renderTags}
        getOptionLabel={getOptionLabel}
        componentsProps={componentsProps}
        filterOptions={resolvedFilterOptions}
        openOnFocus
        blurOnSelect={blurOnSelect}
        renderOption={renderOption}
        size={size}
        disabled={disabled || locked}
        disableCloseOnSelect={disableCloseOnSelect}
        ChipProps={ChipProps}
        disableClearable={disableClearable}
        loading={loading}
        loadingText={resolvedLoadingText}
        noOptionsText={resolvedNoOptionsText}
      />
      {helpTextFunc(error, success) && (
        <HelpText
          text={helpTextFunc(error, success)}
          tone={error ? 'error' : success ? 'success' : 'neutral'}
        />
      )}
    </Box>
  );
};
